import { Navigate } from 'react-router-dom';
import { useAuth } from '../stores/auth';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { LoadingScreen } from './States';

/**
 * FeatureGate
 * ---------------------------------------------------------------------
 * Wraps a route element.  If the current org has the specified feature
 * disabled (via `feature_flags`), the user is redirected to the
 * Restricted page with `reason=feature-disabled&feature=<key>` so we can
 * show a helpful message.  Super admins bypass automatically since the
 * hook returns an all-enabled map for platform-only super-admin sessions;
 * for a super admin *impersonating* an org (viewingOrgId), the org's real
 * flags are used — but the backend middleware bypasses super-admin so
 * they can always fix things.
 *
 * While flags are loading the guard shows a lightweight loading screen
 * rather than momentarily rendering the page (which would flash disallowed
 * data before redirecting).
 */
export function FeatureGate({ feature, children }) {
  const { profile, org } = useAuth();
  const { flags, isLoading } = useFeatureFlags();

  // Super admin: never blocked
  if (profile?.role === 'super_admin') return children;
  // VOM with no client org selected (e.g. own credentials folder): there is no
  // org whose flags could apply, so don't hold the page in a loading state.
  if (profile?.role === 'virtual_ops_manager' && !org?.id) return children;

  if (isLoading) return <LoadingScreen />;

  if (flags && flags[feature] === false) {
    return (
      <Navigate
        to={`/restricted?reason=feature-disabled&feature=${encodeURIComponent(feature)}`}
        replace
      />
    );
  }

  return children;
}
